/**
 * 챗봇 API 호출 모듈.
 *
 * 일반 GET/POST는 표준 api 클라이언트(axios) 사용.
 * 메시지 전송은 SSE streaming 이라 fetch + ReadableStream 으로 자체 구현.
 *
 * SSE 이벤트 타입:
 *   - token  : {text: "..."}   응답 청크
 *   - done   : {messageId, remainingToday, promptTokens, completionTokens, totalTokens}
 *   - error  : {code, message}
 *
 * EventSource 안 쓰는 이유: EventSource 는 GET 만 지원. 우리는 POST + SSE 응답 필요.
 */
import { api } from "../../../util/api";

// /api 포함된 베이스 — `/api/chatbot/...` 으로 매핑된 컨트롤러와 일치.
// streaming 은 fetch 로 직접 호출하므로 RAW(/api 없음) 도 따로 보관.
const API_BASE = process.env.REACT_APP_API_BASE_URL;
const RAW_BASE = process.env.REACT_APP_API_REAL_URL;

export const chatbotApi = {
    /**
     * 본인 토큰 사용량 조회.
     * @returns {Promise<{todayUsed, monthUsed, dailyLimit, remainingToday}>}
     */
    getUsage: () => api.get(`${API_BASE}/chatbot/usage`),

    /**
     * 본인 히스토리 페이지 — cursor 기반 무한 스크롤.
     * @param {{ beforeId?: number, limit?: number }} params
     * @returns {Promise<{messages: Array, hasMore: boolean, oldestId: number|null}>}
     */
    getHistory: ({ beforeId, limit = 30 } = {}) => {
        const params = { limit };
        if (beforeId != null) params.beforeId = beforeId;
        return api.get(`${API_BASE}/chatbot/history`, params);
    },

    /** 컨텍스트 리셋 — SYSTEM_RESET 마커 INSERT. */
    reset: () => api.post(`${API_BASE}/chatbot/reset`),

    /**
     * 메시지 전송 — SSE streaming.
     *
     * @param {string} content 사용자 입력
     * @param {object} callbacks
     * @param {(text:string) => void} callbacks.onToken     매 청크 호출
     * @param {(done:object) => void} callbacks.onDone      완료 시 호출 (messageId, tokens 등)
     * @param {(err:{code, message}) => void} callbacks.onError 에러 SSE 이벤트
     * @returns {AbortController} 호출자가 abort 가능
     */
    streamMessage: (content, { onToken, onDone, onError }) => {
        const ctrl = new AbortController();

        const token = localStorage.getItem("auth_token");
        const url = `${RAW_BASE}/api/chatbot/message`;

        (async () => {
            let response;
            try {
                response = await fetch(url, {
                    method: "POST",
                    signal: ctrl.signal,
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        ...(token ? { "X-TOKEN": token } : {}),
                    },
                    body: JSON.stringify({ content }),
                });
            } catch (e) {
                if (e.name === "AbortError") return;
                onError?.({ code: "NETWORK_ERROR", message: "네트워크 오류: " + e.message });
                return;
            }

            if (!response.ok) {
                // 일반 HTTP 에러 (인증 만료 등)
                let msg = "요청 실패";
                try {
                    const json = await response.json();
                    msg = json?.message || msg;
                } catch (_) {}
                onError?.({ code: `HTTP_${response.status}`, message: msg });
                return;
            }

            // SSE 라인 단위 파싱
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            let currentEvent = "message";
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    let idx;
                    while ((idx = buffer.indexOf("\n")) >= 0) {
                        const line = buffer.slice(0, idx).replace(/\r$/, "");
                        buffer = buffer.slice(idx + 1);

                        if (line === "") {
                            // 이벤트 경계 — 다음 이벤트 default 로 리셋
                            currentEvent = "message";
                            continue;
                        }
                        if (line.startsWith("event:")) {
                            currentEvent = line.slice(6).trim();
                        } else if (line.startsWith("data:")) {
                            const data = line.slice(5).trim();
                            try {
                                const parsed = JSON.parse(data);
                                if (currentEvent === "token") {
                                    onToken?.(parsed.text || "");
                                } else if (currentEvent === "done") {
                                    onDone?.(parsed);
                                } else if (currentEvent === "error") {
                                    onError?.(parsed);
                                }
                            } catch (_) {
                                // 파싱 실패 청크 무시
                            }
                        }
                    }
                }
            } catch (e) {
                if (e.name !== "AbortError") {
                    onError?.({ code: "STREAM_ERROR", message: "스트리밍 오류: " + e.message });
                }
            }
        })();

        return ctrl;
    },
};

export const adminChatbotApi = {
    /**
     * 전 직원 사용 통계 — 관리자(HR) 권한.
     * @param {{ from: string, to: string, deptId?: number, employeeId?: number }} params
     */
    getUsage: ({ from, to, deptId, employeeId }) => {
        const params = { from, to };
        if (deptId != null) params.deptId = deptId;
        if (employeeId != null) params.employeeId = employeeId;
        return api.get(`${API_BASE}/admin/chatbot/usage`, params);
    },

    /** 직원 일별 추이 — 그래프용. */
    getEmployeeTimeline: ({ employeeId, from, to }) =>
        api.get(`${API_BASE}/admin/chatbot/usage/${employeeId}/timeline`, { from, to }),
};
